import React from "react";
import { Box, Grid, IconButton, Paper, Typography } from "@mui/material";
import { AddressFields, Badge, Field, OutlineButton, SubHeading } from "./formParts";
import { Icon, T } from "./signupDesign";

// 18px between rows, 16px between columns (as the steps).
const GRID = { container: true, rowSpacing: 2.25, columnSpacing: 2 };

const MAX_LOCATIONS = 9;

const blankLocation = () => ({ restaurantId: null, dbaName: "", address1: "", address2: "", city: "", state: "", zip: "", phone: "" });

const oneLine = (l) => [l.address1, l.city, [l.state, l.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

/**
 * Basic info's other restaurants in this agreement (business.locations;
 * backend utils/merchantApplication.js mergeLocations). One agreement covers
 * them only when they share the corporate name, tax ID and bank account.
 * Ones already on RushMyFood are added by a super admin (ownerRestaurants:
 * the owner's others, from the staff details) and the owner can take one
 * out; new ones are typed in here. Errors are "locations.0.city" and so on.
 * readOnly: a signed application. Restaurants can't be added to or taken
 * out of what was signed, so only the list shows (nothing when it is empty).
 */
export default function LocationsSection({ locations, onChange, errors, states, ownerRestaurants = [], grocery, readOnly = false }) {
  const list = Array.isArray(locations) ? locations : [];
  if (readOnly && !list.length) return null;
  const listed = new Set(list.filter((l) => l.restaurantId).map((l) => Number(l.restaurantId)));
  const addable = ownerRestaurants.filter((r) => !listed.has(Number(r.restaurantId)));
  const set = (i, patch) => onChange(list.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const word = grocery ? "store" : "restaurant";

  return (
    <Box component="section" aria-label={`Other ${word}s in this agreement`} sx={{ mt: 1 }}>
      <SubHeading sx={{ mb: 1 }}>{`Other ${word}s in this agreement`}</SubHeading>
      <Typography sx={{ color: T.text2, fontSize: 14.5, lineHeight: 1.6, mb: 2 }}>
        {readOnly
          ? `These ${word}s are in this agreement too, run by the same company and paid into the same bank account.`
          : `Add your other ${word}s here only if they're run by the same company (the same legal name and tax ID) and paid into the same bank account. Each one keeps its own listing, menu and payouts. A ${word} with a different company or bank needs its own agreement.`}
      </Typography>
      {errors.locations && <Typography sx={{ color: T.error, fontSize: 14, mb: 1.5 }}>{errors.locations}</Typography>}

      {list.map((l, i) => {
        const prefix = `locations.${i}.`;
        const label = l.dbaName || `${word.charAt(0).toUpperCase()}${word.slice(1)} ${i + 2}`;
        return (
          <Paper key={l._key || `new-${i}`} elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, borderRadius: "18px", border: `1px solid ${T.cardBorder}`, background: T.card }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: l.restaurantId ? 0 : 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: 17, color: T.text, overflowWrap: "anywhere" }}>{label}</Typography>
                  {l.restaurantId && <Badge kind="done">Already on RushMyFood</Badge>}
                </Box>
                {l.restaurantId && <Typography sx={{ color: T.hint, fontSize: 14, mt: 0.5 }}>{oneLine(l)}</Typography>}
              </Box>
              {!readOnly && (
                <IconButton aria-label={`Take ${label} out of this agreement`} onClick={() => remove(i)} sx={{ width: 44, height: 44, color: T.muted, flexShrink: 0 }}>
                  <Icon.Trash size={20} />
                </IconButton>
              )}
            </Box>
            {!l.restaurantId && (
              <Grid {...GRID}>
                <Field label={grocery ? "Store name (DBA)" : "Restaurant name (DBA)"} name={`${prefix}dbaName`} value={l.dbaName} onChange={(v) => set(i, { dbaName: v })} errors={errors} required sm={7} />
                <Field label="Phone number" name={`${prefix}phone`} type="tel" value={l.phone} onChange={(v) => set(i, { phone: v })} errors={errors} required sm={5} />
                <AddressFields value={l} onChange={(a) => set(i, a)} errors={errors} prefix={prefix} states={states} label={grocery ? "Store address" : "Restaurant address"} />
              </Grid>
            )}
          </Paper>
        );
      })}

      {!readOnly && addable.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: T.text, mb: 1 }}>The owner's other restaurants on RushMyFood</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {addable.map((r) => (
              <OutlineButton key={r.restaurantId} startIcon={<Icon.Plus size={18} />} disabled={list.length >= MAX_LOCATIONS} onClick={() => onChange([...list, { ...r }])}>
                {r.dbaName}
              </OutlineButton>
            ))}
          </Box>
        </Box>
      )}
      {!readOnly && list.length < MAX_LOCATIONS && (
        <OutlineButton startIcon={<Icon.Plus size={18} />} onClick={() => onChange([...list, blankLocation()])}>
          {`Add another ${word}`}
        </OutlineButton>
      )}
    </Box>
  );
}
